import { useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ProposalCard } from './ProposalCard';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/solid';
import type { Phase2Proposal } from '@/lib/services/agent-wizard';

const TONE_OPTIONS = [
  { value: 'professional', label: 'Professional', description: 'Formal, business-appropriate' },
  { value: 'casual', label: 'Casual', description: 'Friendly, conversational' },
  { value: 'consultative', label: 'Consultative', description: 'Advisory, value-focused' },
  { value: 'direct', label: 'Direct', description: 'Short, to the point' },
] as const;

const DELAY_OPTIONS = [
  { value: '1', label: 'Wait 1 day' },
  { value: '2', label: 'Wait 2 days' },
  { value: '3', label: 'Wait 3 days' },
  { value: '4', label: 'Wait 4 days' },
  { value: '5', label: 'Wait 5 days' },
  { value: '7', label: 'Wait 7 days' },
  { value: '10', label: 'Wait 10 days' },
  { value: '14', label: 'Wait 14 days' },
];

interface Phase2Props {
  proposal: Phase2Proposal;
  onChange: (updated: Phase2Proposal) => void;
  hideSequence?: boolean;
}

export function Phase2CampaignSetup({ proposal, onChange }: Phase2Props) {
  const autoResize = useCallback((el: HTMLTextAreaElement | null) => {
    if (el) {
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    }
  }, []);

  const updateStep = (index: number, field: string, value: string | number) => {
    const updated = [...proposal.sequenceSteps];
    updated[index] = { ...updated[index], [field]: value };
    onChange({ ...proposal, sequenceSteps: updated });
  };

  const addStep = () => {
    onChange({
      ...proposal,
      sequenceSteps: [
        ...proposal.sequenceSteps,
        {
          stepIndex: proposal.sequenceSteps.length,
          type: 'email' as const,
          delay: 3,
          delayUnit: 'days' as const,
          subject: '',
          contentSketch: '',
        },
      ],
    });
  };

  const removeStep = (index: number) => {
    onChange({
      ...proposal,
      sequenceSteps: proposal.sequenceSteps
        .filter((_, i) => i !== index)
        .map((step, i) => ({ ...step, stepIndex: i })),
    });
  };

  return (
    <div className="space-y-6">
      <ProposalCard title="Outreach Goal" reasoning={proposal.reasoning}>
        <Textarea
          ref={autoResize}
          value={proposal.outreachGoal}
          onChange={(e) => { onChange({ ...proposal, outreachGoal: e.target.value }); autoResize(e.target); }}
          className="min-h-[60px] resize-none overflow-hidden"
          placeholder="What do you want to achieve? e.g. Book 10 discovery calls per month"
        />
      </ProposalCard>

      <ProposalCard title="Messaging Tone">
        <div className="grid grid-cols-2 gap-2">
          {TONE_OPTIONS.map((tone) => (
            <button
              key={tone.value}
              type="button"
              onClick={() => onChange({ ...proposal, messagingTone: tone.value })}
              className={`text-left p-3 rounded-md border text-sm transition-colors ${
                proposal.messagingTone === tone.value
                  ? 'border-info-solid bg-surface-selected-accent'
                  : 'border-border/50 hover:border-border'
              }`}
            >
              <span className="font-medium">{tone.label}</span>
              <span className="block text-xs text-muted-foreground mt-0.5">{tone.description}</span>
            </button>
          ))}
        </div>
      </ProposalCard>

      <ProposalCard title="Email Sequence">
        <div className="space-y-4">
          {proposal.sequenceSteps.map((step, index) => (
            <div key={index} className="p-4 bg-surface-sunken rounded-xl space-y-3 relative group">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  Email {index + 1}
                  {index === 0 ? ' (Initial)' : ''}
                </span>
                <div className="flex items-center gap-2">
                  {index > 0 && (
                    <Select
                      value={(step.delay || 3).toString()}
                      onValueChange={(value) => updateStep(index, 'delay', parseInt(value))}
                    >
                      <SelectTrigger className="w-auto min-w-[130px] h-8 text-xs border border-control-border rounded-full pl-3 pr-2 shadow-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DELAY_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {proposal.sequenceSteps.length > 1 && (
                    <button
                      onClick={() => removeStep(index)}
                      className="w-6 h-6 rounded-full bg-surface-track hover:bg-danger-surface-strong text-muted-foreground hover:text-danger-text transition-colors flex items-center justify-center cursor-pointer"
                    >
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
              <Input
                value={step.subject}
                onChange={(e) => updateStep(index, 'subject', e.target.value)}
                placeholder="Subject line"
                inlineLabel="Subject"
                className="w-full"
              />
              <Textarea
                ref={autoResize}
                value={step.contentSketch}
                onChange={(e) => { updateStep(index, 'contentSketch', e.target.value); autoResize(e.target); }}
                placeholder="Content approach / angle for this email"
                className="min-h-[60px] text-sm resize-none overflow-hidden"
              />
            </div>
          ))}
          <Button
            variant="secondary"
            size="sm"
            onClick={addStep}
            className="gap-1.5"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Add email step
          </Button>
        </div>
      </ProposalCard>
    </div>
  );
}
